import React, { useMemo, useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "react-bootstrap";
import { BsChevronLeft, BsChevronRight, BsDisplay } from "react-icons/bs";

import "bootstrap/dist/css/bootstrap.min.css";
import CourseCard179 from "../../../shared/components/CourseCard179";

import "../../../shared/styles/CardSlideForm.css";
import "../../../shared/styles/UtillitiesComponent.css";

// Course 2/79 covers, in featured card order.
import C_2_79 from "../../../assets/thaibar-2-79.JPG";
import G_2_79 from "../../../assets/g-2-79.JPG";
import P_2_79 from "../../../assets/p-2-79.JPG";
import M_2_79 from "../../../assets/m-2-79.JPG";
import F_2_79 from "../../../assets/f-2-79.JPG";

function courseHasCategory(course, category) {
  if (!category || category === "all") return true;
  const cats = Array.isArray(course?.categories) ? course.categories : [];
  return cats.includes(category);
}

export default function Course2_79({
  title = "คอร์สแนะนำ",
  moreTo = "/courses",
  moreLabel = "ดูเพิ่มเติม",
  selectedCategory = "all",
}) {
  const courseItems = useMemo(() => {
    return [
      {
        id: "thaibar-2-79-main",
        slug: "คอร์สเนติบัณฑิต-279",
        coverSrc: C_2_79,
        title: "คอร์สเนติบัณฑิต 2/79",
        subTitle: "ฟรีเอกสารประกอบการติวรูปแบบ PDF พร้อมระบบ Hyperlink",
        displayPrice: "ราคาตามแพ็คเกจ",
        category: "เนติบัณฑิต",
        categories: ["เนติบัณฑิต"],
      },
      {
        id: "thaibar-2-79-guarantee",
        slug: "คอร์สเนติบัณฑิต-279",
        coverSrc: G_2_79,
        title: "คอร์สการันตีคืนเงิน 2/79",
        subTitle: "เพิ่มความมั่นใจ เพราะคอร์สนี้เรารับประกัน",
        displayPrice: "8,900 บาท",
        category: "เนติบัณฑิต",
        categories: ["เนติบัณฑิต"],
      },
      {
        id: "thaibar-2-79-perfect",
        slug: "คอร์สเนติบัณฑิต-279",
        coverSrc: P_2_79,
        title: "คอร์สแม่นหลัก+สรุป 2/79",
        subTitle: "เหมาะกับทุกคนที่อยากสอบผ่าน",
        displayPrice: "6,900 บาท",
        category: "เนติบัณฑิต",
        categories: ["เนติบัณฑิต"],
      },
      {
        id: "thaibar-2-79-mainonly",
        slug: "คอร์สเนติบัณฑิต-279",
        coverSrc: M_2_79,
        title: "คอร์สแม่นหลัก 2/79",
        subTitle: "เหมาะกับคนจับจุดไม่ถูก ไม่รู้จะเริ่มยังไง",
        displayPrice: "4,900 บาท",
        category: "เนติบัณฑิต",
        categories: ["เนติบัณฑิต"],
      },
      {
        id: "thaibar-2-79-summary",
        slug: "คอร์สเนติบัณฑิต-279",
        coverSrc: F_2_79,
        title: "คอร์สสรุปก่อนสอบ 2/79",
        subTitle: "เหมาะกับคนไม่มีเวลาตามคำบรรยาย และเพิ่มความมั่นใจก่อนเข้าสอบ",
        displayPrice: "2,900 บาท",
        category: "เนติบัณฑิต",
        categories: ["เนติบัณฑิต"],
      },
    ];
  }, []);

  const coursesSorted = useMemo(() => {
    return courseItems.filter((c) => courseHasCategory(c, selectedCategory));
  }, [courseItems, selectedCategory]);

  const slideRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const initialScrollRef = useRef(null);

  useEffect(() => {
    const el = slideRef.current;
    if (!el) return;

    el.scrollLeft = 0;
    initialScrollRef.current = null;

    const updateArrows = () => {
      const now = slideRef.current;
      if (!now) return;

      const { scrollLeft, scrollWidth, clientWidth } = now;
      if (initialScrollRef.current === null) initialScrollRef.current = scrollLeft;

      if (scrollWidth <= clientWidth + 1) {
        setCanScrollLeft(false);
        setCanScrollRight(false);
        return;
      }

      const base = initialScrollRef.current ?? 0;
      const atStart = scrollLeft <= base + 1;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      setCanScrollLeft(!atStart);
      setCanScrollRight(!atEnd);
    };

    updateArrows();
    el.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);

    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [coursesSorted.length, selectedCategory]);

  const handleSlideNext = () => {
    const el = slideRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth * 0.8, behavior: "smooth" });
    setHasInteracted(true);
  };

  const handleSlidePrev = () => {
    const el = slideRef.current;
    if (!el) return;
    el.scrollBy({ left: -el.clientWidth * 0.8, behavior: "smooth" });
    setHasInteracted(true);
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h3 className="mb-0 d-flex align-items-center gap-2">
          <BsDisplay />
          <strong>{title}</strong>
        </h3>

        <Link to={moreTo} style={{ textDecoration: "none" }}>
          <Button variant="link" className="p-0 text-decoration-none">
            {moreLabel} <span aria-hidden="true">›</span>
          </Button>
        </Link>
      </div>

      {coursesSorted.length === 0 ? (
        <div className="text-muted">ยังไม่มีคอร์สให้แสดง</div>
      ) : (
        <div
          className={`slide-wrapper ${
            canScrollLeft || canScrollRight ? "has-scroll" : "no-scroll"
          }`}
        >
          {hasInteracted && canScrollLeft && (
            <button
              type="button"
              className="slide-arrow slide-arrow-left"
              onClick={handleSlidePrev}
              aria-label="เลื่อนซ้าย"
            >
              <BsChevronLeft size={34} />
            </button>
          )}

          <div
            className="article-slide-container"
            ref={slideRef}
            onTouchStart={() => setHasInteracted(true)}
            onMouseDown={() => setHasInteracted(true)}
          >
            {coursesSorted.map((item) => (
              <div key={item.id} className="slide-item">
                <Link
                  to={`/courses/${item.slug}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <CourseCard179 item={item} />
                </Link>
              </div>
            ))}
          </div>

          {canScrollRight && (
            <button
              type="button"
              className="slide-arrow slide-arrow-right"
              onClick={handleSlideNext}
              aria-label="เลื่อนขวา"
            >
              <BsChevronRight size={34} />
            </button>
          )}
        </div>
      )}

      <br />
    </>
  );
}